/**
 * Сторож версії застосунку.
 *
 * Навіщо: у цеху стався реальний випадок — швея сканувала картку й діставала
 * «не знайдено серед доступних», хоча крій готовий. Виявилось, що на її
 * (старому, з поганим звʼязком) телефоні працювала версія тижневої давності:
 * service worker при невдалому запиті віддає збережену сторінку, а та тягне
 * старі скрипти з кешу. Сама вона про це дізнатись не могла.
 *
 * Як працює: раз на 5 хв (і при поверненні на вкладку / появі мережі) тягнемо
 * `/version.json` повз кеш і порівнюємо з версією, з якою зібрано цей код.
 * Якщо різні — показуємо смугу «Доступна нова версія». Дотик = чистимо кеші,
 * знімаємо service worker і перезавантажуємось начисто.
 *
 * Оновлюється САМО (Danyil 15.10.2026: «щоб не треба було натискати»), але
 * лише в безпечну мить — коли нічого не губиться:
 *   • жоден екран не тримає незбереженого (`hasUnsaved`);
 *   • не відкрите жодне вікно / шторка (там можуть бути введені дані);
 *   • курсор не в полі вводу (людина саме друкує);
 *   • немає мережі — ні (чистка кешу вбила б офлайн-роботу);
 *   • жоден запит не чекає відповіді, памʼятна черга офлайну порожня;
 *   • на екрані немає непрочитаного повідомлення;
 *   • людина хвилину нічого не робила АБО вкладка у фоні;
 *   • це не робоче місце цеху (там — лише смуга з кнопкою).
 * Інакше — смуга «оновиться саме, щойно завершите» + кнопка «Оновити зараз».
 * Після перезавантаження повертаємо скрол на те саме місце тієї ж сторінки.
 */
import { useCallback, useEffect, useState } from "react";
import { useAuthStore } from "@/store/auth";
import { reloadToNewVersion } from "@/lib/staleBuild";
import { hasUnsaved } from "@/lib/unsaved";
import { hasVolatileQueue } from "@/lib/offlineQueue";
import { hasInflight } from "@/api/client";
import { useToastStore } from "@/store/toast";

declare const __BUILD_ID__: string;

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const MIN_GAP_MS = 60 * 1000;
// Спільний гвард: focus/online можуть спрацьовувати десятки разів за зміну на
// слабкому звʼязку — без нього кожен фокус вкладки бив би мережу (version.json
// + reg.update). 5-хв інтервал лишається основним джерелом.
let lastCheck = 0;

const IDLE_MS = 60_000;
// Скільки оновлення пробує спрацювати ТИХО, перш ніж показати смугу (Danyil
// 04.11.2026: «чому знову синя лінія „Оновити“»). Смуга одразу після кожної
// викатки миготіла людині, яка просто працює, хоча за хвилину без дій сайт
// оновився б сам. Робочі місця цеху — одразу: там сам не оновлюється.
const QUIET_MS = 10 * 60 * 1000;
const SAFE_POLL_MS = 5_000;
const SCROLL_KEY = "mes:update-scroll";
let lastActivity = Date.now();

// Робочі місця цеху: там вибране й введене живе в стані екрана без реєстру
// «незбереженого» (вибрані клітинки крою, пачка на упаковці, дефект в ОТК) —
// самі НЕ оновлюємо, лише смуга з кнопкою, як і було (код-ревю 15.10.2026).
const SHOP_FLOOR = /^\/(sewing|cutting|packing|qc|shipping|vto|tsyklichka|terminal|workspace|printing|upload)(\/|$)/;

/** Чи можна перезавантажити сторінку, нічого не загубивши. */
function safeToReload(): boolean {
  // Без мережі — ні: чистка кешу вбила б офлайн-роботу (lib/staleBuild).
  if (!navigator.onLine) return false;
  if (SHOP_FLOOR.test(location.pathname)) return false;
  // Пам'ятна черга не переживе перезавантаження; IndexedDB — переживе.
  if (hasUnsaved() || hasVolatileQueue() || hasInflight()) return false;
  // Повідомлення, яке людина ще не прочитала (липкі «НЕ записано…»), — не стираємо.
  if (useToastStore.getState().items.length > 0) return false;
  // Вікно, шторка, відкритий список вибору (Select — role="listbox").
  if (document.querySelector('[role="dialog"], [aria-modal="true"], [role="listbox"], [role="menu"], .fixed.inset-0:not([data-reload-ok])')) return false;
  const el = document.activeElement as HTMLElement | null;
  if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return false;
  return document.hidden || Date.now() - lastActivity > IDLE_MS;
}

/** Скрол — щоб після оновлення людина була там само, де й була. */
function rememberScroll(): void {
  try {
    sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ path: location.pathname + location.search, y: window.scrollY }));
  } catch {
    /* немає сховища — просто без повернення скролу */
  }
}
function restoreScroll(): void {
  let saved: { path: string; y: number } | null = null;
  try {
    saved = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || "null");
    sessionStorage.removeItem(SCROLL_KEY);
  } catch {
    return;
  }
  if (!saved || saved.path !== location.pathname + location.search || !saved.y) return;
  // Вміст вантажиться не одразу — пробуємо кілька разів, поки висота дозволить.
  // Починаємо ПІСЛЯ ScrollToTop (App.tsx тягне вгору до 900 мс), інакше він
  // затирає відновлене.
  let tries = 0;
  const go = () => {
    window.scrollTo(0, saved!.y);
    if (Math.abs(window.scrollY - saved!.y) > 4 && ++tries < 10) setTimeout(go, 300);
  };
  setTimeout(go, 1100);
}

interface Remote {
  build: string;
  version?: string;
  notes?: string[];
}

async function fetchRemoteBuild(): Promise<Remote | null> {
  try {
    const r = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!r.ok) return null;
    const d = (await r.json()) as Partial<Remote>;
    return d.build ? (d as Remote) : null;
  } catch {
    return null; // немає мережі — не наша проблема, спробуємо пізніше
  }
}

export function UpdateWatcher() {
  const [stale, setStale] = useState(false);
  const [busy, setBusy] = useState(false);
  // Сховано «до наступного разу». Наступна перевірка (5 хв або повернення на
  // вкладку) знову зніме прапорець — тобто це відкладення, а не відмова.
  const [hidden, setHidden] = useState(false);
  // Версія й «що змінено» — лише керівним (власник 28.09.2026): цеху це шум.
  const [remote, setRemote] = useState<Remote | null>(null);
  const [showNotes, setShowNotes] = useState(false);
  // Тиха пора минула — тепер можна показати смугу.
  const [loud, setLoud] = useState(false);
  const isManager = useAuthStore((st) => st.hasAnyRole("admin", "master", "menedzher", "buhgalter"));

  const check = useCallback(async () => {
    if (!import.meta.env.PROD) return;
    if (Date.now() - lastCheck < MIN_GAP_MS) return;
    lastCheck = Date.now();
    // Джерело істини — version.json. reg.update() нижче по замороженому
    // /sw.js?v=<build> байт-ідентичний, тож апдейт SW сам НЕ виявить — не
    // покладаємось на нього, лишаємо лише як підказку браузеру.
    const got = await fetchRemoteBuild();
    if (got && got.build !== __BUILD_ID__) {
      setRemote(got);
      setStale(true);
      setHidden(false);
    }
    // Смуга — лише якщо тихо оновитись не вдалось за QUIET_MS (див. вище).
    try {
      const reg = await navigator.serviceWorker?.getRegistration();
      await reg?.update();
    } catch {
      /* не критично */
    }
  }, []);

  useEffect(() => {
    restoreScroll();
    const touch = () => { lastActivity = Date.now(); };
    const evs = ["pointerdown", "keydown", "wheel", "touchstart", "input"] as const;
    evs.forEach((e) => window.addEventListener(e, touch, { passive: true, capture: true }));
    return () => {
      evs.forEach((e) => window.removeEventListener(e, touch, { capture: true }));
    };
  }, []);

  // Вийшла нова версія — чекаємо безпечної миті й оновлюємось самі.
  // «Пізніше» — справжнє відкладення: до наступної перевірки не чіпаємо.
  useEffect(() => {
    if (!stale || busy || hidden) return;
    const tryNow = () => {
      if (!safeToReload()) return;
      setBusy(true);
      rememberScroll();
      // Не force: staleBuild сам перевірить мережу й нову збірку на сервері.
      void reloadToNewVersion(false).then((went) => { if (!went) setBusy(false); });
    };
    const id = setInterval(tryNow, SAFE_POLL_MS);
    document.addEventListener("visibilitychange", tryNow);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", tryNow);
    };
  }, [stale, busy, hidden]);

  useEffect(() => {
    check();
    const id = setInterval(check, CHECK_INTERVAL_MS);
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onFocus);
    };
  }, [check]);

  async function reloadClean() {
    if (busy) return;
    setBusy(true);
    rememberScroll();
    // Та сама чистка, що й для застарілої вкладки (lib/staleBuild) — одна
    // копія: знімаємо service worker і кеші, інакше повернеться стара оболонка.
    await reloadToNewVersion(true);
  }

  // Банер прибитий до низу екрана, а там уже живе нижня навігація цеху
  // (таб-бар швеї, таб-бар WorkerShell, панель дій закрійника, «Виклик
  // майстра»). Тому МІСЦЕ під нього резервуємо на `body`, а не воюємо
  // z-index'ами: інакше банер просто лягає поверх кнопок, і поки людина не
  // оновиться — низ екрана зайнятий назавжди. Зразок такої компенсації вже є
  // в `index.css` для смуги «вхід під чужим акаунтом».
  useEffect(() => {
    if (!stale) { setLoud(false); return; }
    if (SHOP_FLOOR.test(location.pathname)) { setLoud(true); return; }
    const t = window.setTimeout(() => setLoud(true), QUIET_MS);
    return () => window.clearTimeout(t);
  }, [stale]);

  useEffect(() => {
    const on = stale && !hidden && loud;
    document.body.classList.toggle("has-update-banner", on);
    return () => document.body.classList.remove("has-update-banner");
  }, [stale, hidden, loud]);

  if (!stale || hidden || !loud) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[9997] flex items-center justify-center gap-2 bg-aurora-primary px-3 py-2 text-sm font-semibold text-white shadow-glassXl">
      {isManager && showNotes && (remote?.notes?.length ?? 0) > 0 && (
        <div className="absolute bottom-full right-3 mb-2 w-[min(92vw,420px)] rounded-xl bg-white p-3 text-aurora-ink shadow-glassXl">
          <div className="text-xs font-bold uppercase tracking-wider text-aurora-subtle mb-1.5">
            Що змінено
          </div>
          <ul className="m-0 pl-4 space-y-1 text-sm font-normal list-disc">
            {remote!.notes!.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        </div>
      )}
      <span className="min-w-0">Нова версія — оновиться сама, щойно ви завершите дію</span>
      <button
        onClick={reloadClean}
        disabled={busy}
        className="flex-none min-h-[44px] rounded-full bg-white/25 px-4 font-bold hover:bg-white/35 active:scale-[0.97] transition-transform duration-100 disabled:opacity-60"
      >
        {busy ? "Оновлюю…" : "Оновити зараз"}
      </button>
      {/* «Пізніше» — бо оновлення посеред приймання пачки означає втратити
          заповнену форму. Ховаємо до наступної перевірки, не назавжди. */}
      <button
        onClick={() => setHidden(true)}
        aria-label="Сховати повідомлення про оновлення"
        className="flex-none min-h-[44px] px-2 text-white/80 hover:text-white active:scale-[0.97] transition-transform duration-100"
      >
        Пізніше
      </button>
      {isManager && remote?.version && (
        // Номер версії й «ℹ» — щоб знали, ЩО оновлюють. Навів мишею чи
        // тапнув — список змін над смугою.
        <button
          type="button"
          onClick={() => setShowNotes((v) => !v)}
          // Наведення — лише мишею: на дотику браузер шле «наведення» перед
          // тапом, і список відкривався й одразу закривався (код-ревю 28.09).
          onPointerEnter={(e) => e.pointerType === "mouse" && setShowNotes(true)}
          onPointerLeave={(e) => e.pointerType === "mouse" && setShowNotes(false)}
          aria-expanded={showNotes}
          aria-label="Що змінено в новій версії"
          className="ml-auto flex-none min-h-[44px] inline-flex items-center gap-1.5 px-2 text-xs font-semibold text-white/85 hover:text-white"
        >
          <span className="hidden sm:inline">версія {remote.version}</span>
          <span className="w-5 h-5 inline-flex items-center justify-center rounded-full border border-white/70 text-[11px]">ℹ</span>
        </button>
      )}
    </div>
  );
}
